// @flow strict

import React, { useEffect, useState } from "react";
import { FaWhatsapp } from "react-icons/fa";
import { FiEdit, FiLock, FiMail, FiPhone, FiUser } from "react-icons/fi";
import { format } from "date-fns";
import { motion } from "framer-motion";
import useAuthStore from "./../store/authStore";

import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import InputField from "../components/form/InputField";
import { toast } from "react-toastify";
import axiosToken from "../api/axiosClient";

function ProfilePage() {
  const navigate = useNavigate();
  const { user, isLoading, getUser, logout } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showEditProfilForm, setShowEditProfilForm] = useState(false);
  const [mode, setMode] = useState("update");

  const getValidationSchema = (action) => {
    const baseSchema = {};

    switch (action) {
      case "editProfil":
        baseSchema.nom = Yup.string().required("Nom requis");
        baseSchema.prenom = Yup.string().required("Prénom requis");
        baseSchema.password = Yup.string()
          .min(6, "Minimum 6 caractères")
          .required("Mot de passe requis");

        break;

      case "resetPassword":
        baseSchema.email = Yup.string()
          .email("Email invalide")
          .required("Email requis");
        break;

      case "editPassword":
        baseSchema.password = Yup.string()
          .min(6, "Minimum 6 caractères")
          .required("Mot de passe requis");
        baseSchema.newPassword = Yup.string()
          .min(6, "Minimum 6 caractères")
          .required("Mot de passe requis");
        baseSchema.confirmPassword = Yup.string()
          .oneOf(
            [Yup.ref("newPassword")],
            "Les mots de passe ne correspondent pas"
          )
          .required("Confirmation requise");
        break;

      default:
        break;
    }

    return Yup.object(baseSchema);
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      nom: user?.nom || "",
      prenom: user?.prenom || "",
      email: user?.email || "",
      password: "",
      newPassword: "",
      confirmPassword: "",
    },

    validationSchema: getValidationSchema(
      showEditProfilForm
        ? "editProfil"
        : showPasswordForm
        ? mode === "update"
          ? "editPassword"
          : "resetPassword"
        : ""
    ),
    onSubmit: async (values) => {
      setLoading(true);

      let data = {};
      let endpoint = "";
      let successMessage = "";
      let errorMessage = "";
      let redirectTo = "";
      let functionTo = null;

      const action = showEditProfilForm
        ? "editProfil"
        : showPasswordForm
        ? mode === "update"
          ? "editPassword"
          : "resetPassword"
        : "";

      switch (action) {
        case "editProfil":
          data = {
            nom: values.nom,
            prenom: values.prenom,
            password: values.password,
          };
          endpoint = "user/update-profil";
          successMessage = "Profil mis à jour avec succès.";
          errorMessage = "Erreur lors de la mise à jour du profil.";
          redirectTo = "/profil";
          functionTo = () => getUser();

          break;

        case "resetPassword":
          data = {
            email: values.email,
          };
          endpoint = "/auth/forgot-password";
          successMessage = "Code de reinitialisation envoyé.";
          errorMessage = "Erreur lors de l'envoi du lien.";
          redirectTo = "/reset-password/profil";
          functionTo = () => {
            sessionStorage.setItem("forgot-email", values.email);
          };
          break;

        case "editPassword":
          data = {
            newPassword: values.newPassword,
            confirmPassword: values.confirmPassword,
            password: values.password,
          };
          endpoint = "user/change-password";
          successMessage = "Mot de passe mis à jour.";
          errorMessage = "Erreur lors de la modification du mot de passe.";
          redirectTo = "/index";
          break;

        default:
          toast.error("Aucune action définie.");
          setLoading(false);
          return;
      }

      try {
        console.log({ endpoint, data });
        const response = await axiosToken.post(endpoint, data);
        toast.success(response?.data?.message || successMessage);
        if (functionTo) {
          functionTo();
        }
        navigate(redirectTo);
      } catch (error) {
        console.error("Erreur API :", error);
        toast.error(error?.response?.data?.error || errorMessage);
      }

      setLoading(false);
    },
  });

  if (isLoading || !user?.id) {
    return (
      <motion.div
        key="loading"
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 50 }}
        transition={{ duration: 0.4 }}
        className="h-screen flex justify-center items-center"
      >
        <div className="flex items-center">
          <div className="animate-spin rounded-full h-4 w-4 border-t-4 border-b-4 border-white mr-1" />
          <span className="font-nunito text-[14px]">Chargement de vos données...</span>
        </div>
      </motion.div>
    );
  }

  document.title = `${user?.role?.name || ""} ${user?.prenom || ""} ${
    user?.nom || ""
  } || Profil`;

  return (
    <div className="h-screen-minus-navbar max-h-screen bg-gradient-to-br from-primary to-secondary-100 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-2xl bg-white text-gray-800 shadow-2xl p-8">
        <div className="mb-6 text-center">
          <span className="font-poppins font-medium text-[15px] bg-secondary-50 text-secondary-700 px-3 py-1">
            {user?.role?.name}
          </span>

          <div className="flex justify-center items-center gap-3 mt-3">
            <FiUser size={24} className="text-secondary" />
            <h2 className="font-poppins font-semibold text-[18px] text-title">
              {user?.prenom} {user?.nom}
            </h2>
          </div>
        </div>

        {!showPasswordForm && !showEditProfilForm && (
          <div className="space-y-3 font-nunito text-[14px] mb-6">
            <div className="flex items-center gap-2">
              <FiMail /> <strong>Email :</strong> <span>{user?.email}</span>
            </div>
            <div className="flex items-center gap-2">
              <FiPhone /> <strong>Téléphone :</strong>{" "}
              <span>{user?.telephone}</span>
            </div>
            <div className="flex items-center gap-2">
              <FaWhatsapp /> <strong>WhatsApp :</strong>{" "}
              <span>{user?.whatsapp}</span>
            </div>
            <div className="text-gray-500 text-xs mt-4 text-center">
              {user?.created_at && (
                <>
                  Créé le :{" "}
                  {format(new Date(user.created_at), "dd/MM/yyyy à HH:mm")}
                  <br />
                </>
              )}
              {user?.updated_at && (
                <>
                  Mis à jour :{" "}
                  {format(new Date(user.updated_at), "dd/MM/yyyy à HH:mm")}
                </>
              )}
            </div>
          </div>
        )}

        <div className="flex justify-between items-center gap-4">
          <button
            onClick={() => {
              setShowEditProfilForm(!showEditProfilForm);
              setShowPasswordForm(false);
            }}
            className={`flex items-center gap-2  text-white px-4 py-2 transition font-nunito font-semibold text-[14px] ${
              showEditProfilForm
                ? "bg-secondary-700 hover:bg-secondary-800"
                : "bg-black hover:bg-gray-800"
            }`}
          >
            <FiEdit />
            {showEditProfilForm ? "Fermer" : "Modifier le profil"}
          </button>

          <button
            onClick={() => {
              setShowPasswordForm(!showPasswordForm);
              setShowEditProfilForm(false);
            }}
            className={`flex items-center gap-2  text-white px-4 py-2 transition font-nunito font-semibold text-[14px] ${
              showPasswordForm
                ? "bg-secondary-700 hover:bg-secondary-800"
                : "bg-black hover:bg-gray-800"
            }`}
          >
            <FiLock /> {showPasswordForm ? "Fermer" : "Mot de passe"}
          </button>
        </div>

        {showPasswordForm ? (
          <motion.div
            key="showPasswordForm"
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            transition={{ duration: 0.4 }}
            className="mt-6 bg-gray-100 p-4"
          >
            <div className="flex justify-center gap-4 mb-4">
              <button
                onClick={() => setMode("update")}
                className={`px-4 py-2 font-nunito font-semibold text-[14px] ${
                  mode === "update"
                    ? "bg-secondary-700 text-white"
                    : "bg-white border"
                }`}
              >
                Modifier mot de passe
              </button>
              <button
                onClick={() => setMode("forgot")}
                className={`px-4 py-2 font-nunito font-semibold text-[14px] ${
                  mode === "forgot"
                    ? "bg-secondary-700 text-white"
                    : "bg-white border"
                }`}
              >
                Mot de passe oublié
              </button>
            </div>

            {mode === "update" ? (
              <form className="space-y-3" onSubmit={formik.handleSubmit}>
                <InputField
                  formik={formik}
                  type="password"
                  name="password"
                  placeholder="Ancien mot de passe"
                  className="w-full px-4 py-2 border"
                />
                <InputField
                  formik={formik}
                  type="password"
                  name="newPassword"
                  className="w-full px-4 py-2 border"
                  placeholder="Nouveau mot de passe"
                />
                <InputField
                  formik={formik}
                  type="password"
                  name="confirmPassword"
                  className="w-full px-4 py-2 border"
                  placeholder="Confirmer le mot de passe"
                />
                <button
                  type="submit"
                  className="w-full bg-secondary-700 text-white py-2 hover:bg-secondary-800 font-nunito font-semibold text-[14px]"
                >
                  Mettre à jour
                </button>
              </form>
            ) : (
              <form className="space-y-3" onSubmit={formik.handleSubmit}>
                <InputField
                  formik={formik}
                  type="email"
                  name="email"
                  className="w-full px-4 py-2 border"
                  placeholder="Votre email"
                />

                <button
                  type="submit"
                  className="w-full bg-secondary-700 text-white py-2 hover:bg-secondary-800 font-nunito font-semibold text-[14px]"
                >
                  Réinitialiser le mot de passe
                </button>
              </form>
            )}
          </motion.div>
        ) : (
          showEditProfilForm && (
            <motion.div
              key="showEditProfilForm"
              initial={{ opacity: 0, y: -50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
              transition={{ duration: 0.4 }}
              className="mt-6 bg-gray-100 p-4"
            >
              <form className="space-y-3" onSubmit={formik.handleSubmit}>
                <InputField
                  formik={formik}
                  type="text"
                  name="nom"
                  className="w-full px-4 py-2 border"
                  placeholder="Nom"
                />
                <InputField
                  formik={formik}
                  type="text"
                  name="prenom"
                  className="w-full px-4 py-2 border"
                  placeholder="Prénom"
                />
                <InputField
                  formik={formik}
                  type="password"
                  name="password"
                  className="w-full px-4 py-2 border"
                  placeholder="Votre mot de passe"
                />
                <button
                  type="submit"
                  className="w-full bg-secondary-700 text-white py-2 hover:bg-secondary-800 font-nunito font-semibold text-[14px]"
                >
                  Mettre à jour
                </button>
              </form>
            </motion.div>
          )
        )}
      </div>
    </div>
  );
}

export default ProfilePage;